import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { ArrowLeft, BusFront, Loader2, MapPin, LogIn, LogOut, History } from "lucide-react";
import parentApi from "@/lib/parentApi";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const isLive = (b) => b.last_loc_at && Date.now() - new Date(b.last_loc_at).getTime() < 60000;

function PanOnce({ target }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (map && target && !done.current) {
      done.current = true;
      map.panTo(target);
      map.setZoom(14);
    }
  }, [map, target]);
  return null;
}

function BusMarker({ bus }) {
  const [pos, setPos] = useState({ lat: bus.last_lat, lng: bus.last_lng });
  const posRef = useRef({ lat: bus.last_lat, lng: bus.last_lng });

  useEffect(() => {
    const from = { ...posRef.current };
    const to = { lat: bus.last_lat, lng: bus.last_lng };
    if (from.lat === to.lat && from.lng === to.lng) return;
    const start = performance.now();
    const dur = 1800;
    let raf;
    const step = (t) => {
      const k = Math.min((t - start) / dur, 1);
      const e = 1 - Math.pow(1 - k, 3);
      const next = { lat: from.lat + (to.lat - from.lat) * e, lng: from.lng + (to.lng - from.lng) * e };
      posRef.current = next;
      setPos(next);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [bus.last_lat, bus.last_lng]);

  const live = isLive(bus);
  return (
    <AdvancedMarker position={pos} title={`Bus ${bus.bus_number}`}>
      <div className={`relative w-20 drop-shadow-lg ${live ? "" : "grayscale opacity-70"}`} data-testid={`parent-bus-marker-${bus.bus_id}`}>
        <img src="/bus-marker.png" alt={`Bus ${bus.bus_number}`} className="w-full select-none pointer-events-none" draggable={false} />
        <span className="absolute left-[42%] top-[68%] -translate-x-1/2 -translate-y-1/2 rounded-md bg-slate-900/90 px-1.5 py-0.5 text-[11px] font-black leading-none text-white">
          {bus.bus_number}
        </span>
        {live && <span className="absolute -top-1.5 right-0 h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-white animate-pulse" />}
      </div>
    </AdvancedMarker>
  );
}

export default function ParentBusPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const backTo = sp.get("student") ? `/parent/student/${sp.get("student")}` : "/parent";
  const [buses, setBuses] = useState(null);
  const [feed, setFeed] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await parentApi.get("/parent/bus");
      setBuses(data);
      parentApi.get("/parent/bus-feed").then(({ data }) => setFeed(data)).catch(() => {});
    } catch {
      nav("/parent/login");
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("vlog_parent_token")) { nav("/parent/login"); return; }
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [load, nav]);

  const located = (buses || []).filter((b) => b.last_lat != null && b.last_lng != null);
  const seen = new Set();
  const uniqueBuses = located.filter((b) => !seen.has(b.bus_id) && seen.add(b.bus_id));
  const center = located[0] ? { lat: located[0].last_lat, lng: located[0].last_lng } : null;

  return (
    <div className="flex-1 bg-slate-50 flex flex-col" data-testid="parent-bus-page">
      <header className="bg-white border-b border-slate-200 text-slate-900 px-5 py-4">
        <div className="w-full flex items-center gap-3">
          <button onClick={() => nav(backTo)} data-testid="parent-bus-back" className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-700 inline-flex items-center justify-center hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center">
            <BusFront className="h-4.5 w-4.5 text-amber-600" />
          </div>
          <div>
            <div className="font-display font-bold leading-tight">Bus tracker</div>
            <div className="text-[11px] text-slate-500">Live while the driver is sharing GPS</div>
          </div>
        </div>
      </header>

      <div className="h-[52vh] bg-slate-200" data-testid="parent-bus-map">
        {buses === null ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : !MAPS_KEY || !center ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-8">
            <MapPin className="h-8 w-8 text-slate-400 mb-3" />
            <div className="font-medium text-slate-700">No live location yet</div>
            <p className="text-xs text-slate-500 mt-1">The bus will appear here as soon as the driver starts sharing GPS.</p>
          </div>
        ) : (
          <APIProvider apiKey={MAPS_KEY}>
            <Map style={{ width: "100%", height: "100%" }} defaultCenter={center} defaultZoom={13}
                 mapId="logerd-parent-bus" gestureHandling="greedy" disableDefaultUI={true} zoomControl={true}>
              <PanOnce target={center} />
              {uniqueBuses.map((b) => <BusMarker key={b.bus_id} bus={b} />)}
            </Map>
          </APIProvider>
        )}
      </div>

      <main className="w-full px-6 py-4 space-y-3 flex-1">
        {buses !== null && buses.length === 0 && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm text-sm text-slate-500" data-testid="parent-no-bus">
            None of your children are assigned to a school bus yet.
          </div>
        )}
        {(buses || []).map((b) => (
          <div key={b.student_id} className="rounded-2xl bg-white p-4 shadow-sm flex items-center gap-3" data-testid={`parent-bus-card-${b.student_id}`}>
            <div className="h-11 w-11 shrink-0 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <BusFront className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate">{b.student_name}</div>
              <div className="text-xs text-slate-500 truncate">Bus {b.bus_number} · {b.school_name}</div>
              {(b.status === "delayed" || (b.sub_number && b.sub_number !== b.bus_number)) && (
                <div className="mt-1 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-900" data-testid={`parent-bus-status-${b.student_id}`}>
                  {b.sub_number && b.sub_number !== b.bus_number && <div className="font-bold">Today: look for Bus {b.sub_number}</div>}
                  {b.status === "delayed" && <div className="font-semibold">Running {b.delay_minutes ? `about ${b.delay_minutes} min` : ""} late</div>}
                  {b.status_note && <div className="text-amber-800">{b.status_note}</div>}
                </div>
              )}
              <div className="text-[11px] text-slate-400 mt-0.5">
                {b.last_loc_at
                  ? `Location updated ${new Date(b.last_loc_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                  : "Waiting for the driver to share GPS"}
              </div>
            </div>
            <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${
              b.onboard ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
            }`} data-testid={`parent-onboard-${b.student_id}`}>
              {b.onboard && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />}
              {b.onboard ? "On the bus" : "Not on the bus"}
            </span>
          </div>
        ))}

        {feed.length > 0 && (
          <section className="rounded-2xl bg-white shadow-sm overflow-hidden" data-testid="parent-bus-feed">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <History className="h-4 w-4 text-slate-500" />
              <span className="font-display font-bold text-slate-900 text-sm">Bus timeline</span>
              <span className="text-[11px] text-slate-400 ml-auto">last 7 days</span>
            </div>
            <ul className="px-4 py-2">
              {feed.map((ev, i) => {
                const board = ev.event_type === "board";
                const d = ev.occurred_at ? new Date(ev.occurred_at) : null;
                const today = d && d.toDateString() === new Date().toDateString();
                return (
                  <li key={i} className="flex items-start gap-3 py-2.5 relative" data-testid={`parent-feed-event-${i}`}>
                    {i < feed.length - 1 && <span className="absolute left-[15px] top-9 bottom-0 w-px bg-slate-200" />}
                    <span className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center z-10 ${
                      board ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}>
                      {board ? <LogIn className="h-4 w-4" /> : <LogOut className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900">
                        {ev.student_name} {board ? "got on" : "got off"} Bus {ev.bus_number}
                      </div>
                      <div className="text-[11px] text-slate-500">{ev.school_name}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs font-semibold text-slate-700">
                        {d ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {d ? (today ? "Today" : d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })) : ""}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
